import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import OnboardingStepIndicator from './OnboardingStepIndicator';

// Phase 3 UX (Feb 2026): one-time role-specific "you're ready" screen that
// appears after the required onboarding steps (setup + legal). Kept simple
// so we do NOT redesign the authenticated dashboard here — this screen just
// bridges verification → the user's first useful action.
//
// Persistence: we use a "pending" localStorage flag that the onboarding
// setup components set the moment they successfully mark the user
// onboardingCompleted. The gate only renders this screen while that flag
// is present, so already-onboarded returning users never see it. Clicking
// the CTA clears the flag. No new backend fields required.
export const READY_PENDING_KEY = (userId) => `sc365_ready_pending_${userId}`;

export default function AccountReadyStep({ role }) {
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const clickGuardRef = useRef(false);

  const isManager = role === 'manager';
  const title = isManager ? 'Your branch is ready' : 'Your account is ready';
  const subtitle = isManager
    ? 'You can now find cover for your branch.'
    : 'You can now see available shifts.';
  const ctaLabel = isManager ? 'Go to Dashboard' : 'Go to Find Shifts';
  const ctaHref = isManager ? '/manager' : '/worker';

  // iter97 — bulletproof navigation. The previous version relied solely on
  // react-router `navigate(..., {replace:true})`. In rare cases where the
  // OnboardingGate re-runs against a stale user object (e.g. after a
  // token refresh race), the user was bounced back into onboarding.
  //
  // The fix:
  //   1. Set a click-guard so a double-tap can't fire twice.
  //   2. Call `refreshUser()` so the AuthContext holds the very latest
  //      onboardingCompleted / *PromptHandled / *AcknowledgedAt flags.
  //   3. If for any reason the server still reports onboardingCompleted
  //      as false, patch it directly before navigating.
  //   4. Clear the localStorage ready flag AFTER the server is confirmed.
  //   5. Use a hard `window.location.assign(ctaHref)` so the gate
  //      re-evaluates from a clean render tree — this cannot land back
  //      inside the onboarding stack.
  const handleContinue = async () => {
    if (clickGuardRef.current) return;
    clickGuardRef.current = true;
    setBusy(true);
    try {
      // Refresh once so the context has the latest server state before
      // we hard-navigate.
      let fresh = null;
      try { fresh = await refreshUser?.(); } catch { /* non-blocking */ }
      const currentUser = fresh || user;
      // Belt-and-braces: if the server hasn't recorded completion yet
      // (very rare race after a full QA reset), do it now so the gate
      // does not send the user back through step 1.
      if (currentUser && currentUser.onboardingCompleted !== true) {
        try { await api.patch('/users/me/onboarding-complete'); } catch { /* non-blocking */ }
      }
      if (user?.id) {
        try { localStorage.removeItem(READY_PENDING_KEY(user.id)); } catch { /* storage may be blocked */ }
      }
      // Hard navigation guarantees a clean re-mount of the guard.
      window.location.assign(ctaHref);
      // Fallback for environments where assign() is stubbed (tests).
      setTimeout(() => navigate(ctaHref, { replace: true }), 50);
    } finally {
      // Do not reset clickGuardRef — we intentionally block further clicks.
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center p-4" data-testid="account-ready-step">
      <Card className="w-full max-w-md border-0 shadow-lg">
        <CardContent className="p-8 text-center">
          <OnboardingStepIndicator current={5} role={isManager ? 'manager' : 'worker'} />
          <div className={`h-16 w-16 rounded-2xl flex items-center justify-center mx-auto mb-5 ${isManager ? 'bg-[#0f1b3d]' : 'bg-emerald-600'}`}>
            <CheckCircle2 className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2" data-testid="ready-title">{title}</h2>
          <p className="text-base text-slate-600 mb-7" data-testid="ready-subtitle">{subtitle}</p>
          <Button
            onClick={handleContinue}
            disabled={busy}
            className={`w-full h-14 rounded-full font-semibold text-base ${isManager ? 'bg-[#DA291C] hover:bg-[#b91f13]' : 'bg-emerald-600 hover:bg-emerald-700'} text-white disabled:opacity-70`}
            data-testid="ready-cta-btn"
          >
            {busy ? (
              <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Taking you there…</>
            ) : (
              <>{ctaLabel} <ArrowRight className="h-5 w-5 ml-2" /></>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
